
import { Card, CardContent } from "@/components/ui/card";
import { DollarSign } from "lucide-react";
import { ProductCard } from "./ProductCard";

interface ProductPrice {
  id: string;
  barcode: string | null;
  name: string;
  current_price: number | null;
  category: string | null;
  category_name: string | null;
  supplier: string | null;
  last_synced: string;
  square_item_id: string;
  description: string | null;
  inventory_tracking: boolean | null;
  current_stock: number | null;
}

interface SearchResultsProps {
  results: ProductPrice[];
  isLoading: boolean;
  hasSearched: boolean;
}

export const SearchResults = ({ results, isLoading, hasSearched }: SearchResultsProps) => {
  // Show loading state
  if (isLoading) {
    return (
      <Card className="mt-8">
        <CardContent className="text-center py-16">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <h3 className="text-2xl font-semibold mb-2">Searching...</h3>
          <p className="text-lg text-muted-foreground">Looking up product information</p>
        </CardContent>
      </Card>
    );
  }

  // Show results if found
  if (results.length > 0) {
    return (
      <div className="space-y-6 mt-8">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-green-600 mb-2">✅ Product Found</h2>
          <p className="text-lg text-muted-foreground">Found {results.length} matching product{results.length > 1 ? 's' : ''}</p>
        </div>
        {results.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    );
  }

  // Show "Product Not Found" if search was performed but no results
  if (hasSearched && results.length === 0) {
    return (
      <Card className="mt-8 border-2 border-red-200 bg-red-50/50">
        <CardContent className="text-center py-16">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-3xl font-bold text-red-600 mb-4">Product Not Found</h3>
          <p className="text-xl text-red-500 mb-2">No product found with this barcode</p>
          <p className="text-lg text-muted-foreground">
            Check barcode accuracy or sync latest catalog from Square POS
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show ready state when no search has been performed
  return (
    <Card className="mt-8">
      <CardContent className="text-center py-16">
        <DollarSign className="w-20 h-20 mx-auto text-primary mb-6" />
        <h3 className="text-3xl font-bold mb-4">Ready for Scanning</h3>
        <p className="text-xl text-muted-foreground mb-2">Use your hardware scanner or enter barcode manually</p>
        <p className="text-lg text-muted-foreground/70">
          Product data synced from Square POS
        </p>
      </CardContent>
    </Card>
  );
};
